import { Order } from "kmmp";
import { PostPutCopyResponse } from "davenport";
import { CreateOrderRequest, ResubmitImagesRequest, IListResult } from "api";
import { ApiError } from "client/http/base";
import { CONSTANTS } from "client/constants";

const TIMEOUT_MS = 30_000;

async function fetchWithTimeout<T>(
    url: string,
    method: string,
    authToken: string,
    body?: unknown,
    qs?: Record<string, unknown>,
    signal?: AbortSignal,
    priority?: RequestPriority
): Promise<T> {
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), TIMEOUT_MS);
    signal?.addEventListener("abort", () => controller.abort(signal.reason), { once: true });

    let fullUrl = url;
    if (qs) {
        const params = new URLSearchParams(
            Object.entries(qs)
                .filter(([, v]) => v != null)
                .map(([k, v]) => [k, String(v)])
        );
        fullUrl += `?${params.toString()}`;
    }

    const headers: Record<string, string> = {
        [CONSTANTS.AUTH_HEADER_NAME]: authToken,
    };
    if (body !== undefined) {
        headers["Content-Type"] = "application/json";
    }

    try {
        const response = await fetch(fullUrl, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
            signal: controller.signal,
            // Supported in Chrome 101+, Safari 17.2+, and Firefox 132+ (Oct 2024).
            // "high" tells the browser to schedule this fetch ahead of image downloads that
            // share the same HTTP/2 connection, reducing head-of-line blocking on slow connections.
            priority,
        });

        if (!response.ok) {
            let bodyData: any = null;
            try {
                bodyData = await response.json();
            } catch {
                // non-JSON response
            }
            const message =
                (bodyData && (bodyData.message || bodyData.error)) ||
                `${method} request to ${fullUrl} failed with ${response.status} ${response.statusText}`;
            throw new ApiError(response.status, response.statusText, message);
        }

        return response.json() as Promise<T>;
    } catch (error) {
        if (error instanceof ApiError) throw error;

        if (error instanceof DOMException && error.name === "AbortError") {
            throw new ApiError(
                0,
                "Request Timed Out",
                `The server did not respond within ${TIMEOUT_MS / 1000} seconds. Please try again.`
            );
        }

        throw new ApiError(
            503,
            "Service Unavailable",
            error instanceof Error ? error.message : "Failed to complete request."
        );
    } finally {
        clearTimeout(timerId);
    }
}

class EndpointV1 {
    private readonly base = "/api/v1/orders";

    constructor(private authToken: string) {}

    public list = (options?: { limit?: number; skip?: number; descending?: boolean }, signal?: AbortSignal) =>
        fetchWithTimeout<IListResult<Order>>(this.base, "GET", this.authToken, undefined, options as Record<string, unknown>, signal, "high");

    public getByDisplayId = (id: number) =>
        fetchWithTimeout<Order>(`${this.base}/${id}`, "GET", this.authToken);
}

class EndpointV2 {
    private readonly base = "/api/v2/orders";

    constructor(private authToken: string) {}

    public create = (data: CreateOrderRequest) =>
        fetchWithTimeout<PostPutCopyResponse & { display_id: number; email: string | boolean }>(
            this.base,
            "POST",
            this.authToken,
            data,
            undefined,
            undefined,
            "high"
        );

    public resubmitImages = (orderId: number, resubmittedImages: ResubmitImagesRequest.LineItemImage[]) => {
        const data: ResubmitImagesRequest = resubmittedImages;

        return fetchWithTimeout<Order>(`${this.base}/${orderId}/resubmit-images`, "PUT", this.authToken, data);
    };
}

export class Orders {
    constructor(protected authToken: string) {
        this.V1 = new EndpointV1(authToken);
        this.V2 = new EndpointV2(authToken);
    }

    public V1: EndpointV1;
    public V2: EndpointV2;
}
